// 网络重试机制：指数退避策略实现 - 参考 adjust web sdk 优化

import { HttpError, RetryConfig, RetryInfo } from './http/types';

const DEFAULT_MAX_RETRY = 3;

export class ExponentialBackoff {
  private config: RetryConfig;
  private totalAttempts: number = 0;        // 总重试次数（包括初始请求）
  private connectionAttempts: number = 0;   // 连接错误重试次数
  private startTime: number = 0;
  private timeoutId: number | null = null;

  constructor(config: Partial<RetryConfig> = {}) {
    this.config = { maxRetries: DEFAULT_MAX_RETRY, ...config };
  }
  /**
     * 执行带重试的异步操作 - 参考 adjust web sdk 的生命周期管理
     */
  async execute<T>(
    operation: () => Promise<T>,
  ): Promise<T> {
    this.reset();
    this.startTime = Date.now();
    while (this.totalAttempts <= this.config.maxRetries) {
      try {
        const result = await operation();
        this.reset();
        return result;
      } catch (error) {
        const httpError = error as HttpError;

        // 检查是否应该重试
        if (!this.shouldRetry(httpError)) {
          this.reset();
          throw httpError;
        }

        this.totalAttempts++;

        // 检查是否超过最大重试次数
        if (this.totalAttempts > this.config.maxRetries) {
          if (this.config.onRetryFailed) {
            this.config.onRetryFailed(httpError, this.totalAttempts);
          }
          this.reset();
          throw httpError;
        }

        // 判断是否为连接错误
        if (this.isConnectionError(httpError)) {
          this.connectionAttempts++;
        }
        // 执行重试前回调
        if (this.config.onRetry) {
          this.config.onRetry(httpError, this.totalAttempts);
        }
      }
    }

    throw new Error('Max retries exceeded');
  }

  /**
     * 重置重试状态
     */
  private reset(): void {
    this.totalAttempts = 0;
    this.connectionAttempts = 0;
    this.startTime = 0;
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  /**
     * 判断是否应该重试
     */
  private shouldRetry(error: HttpError): boolean {
    // 连接错误（CORS、断网等）
    if (this.isConnectionError(error)) {
      return true;
    }

    // 超时错误
    if (this.isTimeout(error)) {
      return true;
    }

    // HTTP 5xx 错误
    if (error.status && error.status >= 500 && error.status < 600) {
      return true;
    }

    // 4xx 错误通常不重试，除非是特定的临时错误
    return false;
  }

  /**
     * 判断是否为连接错误
     */
  private isConnectionError(error: HttpError): boolean {
    return error.message.includes('network') ||
      error.message.includes('connection') ||
      error.message.includes('NO_CONNECTION') ||
      error.status === 0;
  }

  /**
    * 判断是否为超时
    */
  private isTimeout(error: HttpError): boolean {
    return error.message.includes('timeout') || error.message.includes('abort') || error.message.includes('TIMEOUT')
  }

  /**
     * 获取总耗时
     */
  private getTotalTime(): number {
    return this.startTime ? Date.now() - this.startTime : 0;
  }

  /**
     * 获取重试结果信息
     */
  getRetryInfo(error?: HttpError): RetryInfo {
    return {
      success: !error,
      attempts: this.totalAttempts,                     // 总尝试次数（包括初始请求）
      totalTime: this.getTotalTime(),
      connectionRetries: this.connectionAttempts,       // 连接错误重试次数
      requestRetries: this.totalAttempts - this.connectionAttempts  // 非连接错误重试次数
    };
  }
}
